import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export const RegisterPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: location.state?.role || 'guest',
    phone: location.state?.phone || '',
    hotel_name: '',
    hotel_address: '',
    city_area: '',
    hotel_category: '',
    property_type: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(formData);
    setLoading(false);
    if (result.success) {
      toast.success('Registration successful!');
      navigate('/');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-lg">
          <h2 className="text-3xl font-black tracking-tighter text-slate-900 mb-6">{t('auth.createAccount')}</h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="role">{t('auth.iAmA')}</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guest" data-testid="role-guest">{t('auth.roleGuest')}</SelectItem>
                  <SelectItem value="hotel" data-testid="role-hotel">{t('auth.roleHotel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">{t('auth.fullName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">{t('auth.phone')} (optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={(e) => {
                  let v = e.target.value;
                  if (v && !v.startsWith('+91') && !v.startsWith('+')) {
                    v = '+91' + v.replace(/^\+?91?/, '');
                  }
                  setFormData({ ...formData, phone: v });
                }}
                onFocus={() => { if (!formData.phone) setFormData({ ...formData, phone: '+91' }); }}
                data-testid="phone-input"
              />
            </div>

            {formData.role === 'hotel' && (
              <>
                <div>
                  <Label htmlFor="hotel_name">Hotel Name</Label>
                  <Input
                    id="hotel_name"
                    value={formData.hotel_name}
                    onChange={(e) => setFormData({ ...formData, hotel_name: e.target.value })}
                    required
                    data-testid="hotel-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hotel_address">Hotel Address</Label>
                  <Input
                    id="hotel_address"
                    value={formData.hotel_address}
                    onChange={(e) => setFormData({ ...formData, hotel_address: e.target.value })}
                    required
                    data-testid="hotel-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city_area">City/Area</Label>
                  <Input
                    id="city_area"
                    value={formData.city_area}
                    onChange={(e) => setFormData({ ...formData, city_area: e.target.value })}
                    required
                    data-testid="city-area-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hotel_category">Hotel Category</Label>
                  <Select value={formData.hotel_category} onValueChange={(value) => setFormData({ ...formData, hotel_category: value })}>
                    <SelectTrigger data-testid="hotel-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="budget">Budget</SelectItem>
                      <SelectItem value="mid-range">Mid-Range</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="property_type">Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(value) => setFormData({ ...formData, property_type: value })}>
                    <SelectTrigger data-testid="property-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="resort">Resort</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full bg-[#FF0059] hover:bg-[#CC0047]"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? t('auth.creating') : t('auth.createAccount')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-[#FF0059] hover:underline font-bold" data-testid="login-link">
              {t('auth.signIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};